import { TestLocators } from 'constants/Locators';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CustomLink, MUI, ThemeMode } from 'ci-esi-web-shared';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { TrackVirtualPageView } from 'app/components/common/TrackVirtualPageView/TrackVirtualPageView';
import { selectErrorMessage } from 'app/containers/PrivateRoute/selectors';
import { colors } from 'styles/theme/colors';
import { getCallingUrl } from 'utils/routes';

import { Accordion } from './Accordion';
import { Details } from './Details';
import { Summary } from './Summary';

export function ErrorPage() {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const errorMessage = useSelector(selectErrorMessage);
  const [expanded, setExpanded] = useState(false);
  const handleClose = () => {
    window.location.href = getCallingUrl();
  };
  const toggleExpanded = () => setExpanded(!expanded);

  if (!errorMessage) {
    handleClose();
  }

  return (
    <>
      <Helmet>
        <title>{t('ERROR.META_TITLE')}</title>
      </Helmet>
      <TrackVirtualPageView pageTitle={t('ERROR.META_TITLE')} />
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          left: 0,
          right: 0,
        }}
      >
        <div
          style={{
            minWidth: '800px',
            background: isDarkMode ? colors.grey60 : colors.primaryWhite,
            opacity: isDarkMode ? 0.85 : 1,
            padding: '24px',
            boxShadow: '0px 16px 16px 0 #00000024',
            borderRadius: '1px',
          }}
        >
          <div style={{ color: isDarkMode ? colors.primaryWhite : colors.grey40 }}>
            <MUI.Typography
              sx={{
                fontWeight: (theme.typography.fontWeightRegular ?? 400) as number,
                fontSize: '24px',
                textAlign: 'center',
                marginBottom: 2.5,
              }}
            >
              {t('ERROR.TITLE')}
            </MUI.Typography>
            <MUI.Divider
              sx={{
                background: isDarkMode ? colors.grey44 : colors.grey93,
                marginLeft: -3,
                marginRight: -3,
              }}
            />
            <div style={{ marginBottom: 24, marginTop: 24 }}>
              <MUI.Typography
                component="span"
                sx={{ fontSize: '16px', fontWeight: (theme.typography.fontWeightRegular ?? 400) as number }}
                data-testid={TestLocators.NoAccess_Msge}
              >
                {t('ERROR.DESCRIPTION')}
              </MUI.Typography>
            </div>
            <div style={{ marginBottom: 24, marginTop: 24 }}>
              <MUI.Typography
                component="span"
                sx={{ fontSize: '16px', fontWeight: (theme.typography.fontWeightRegular ?? 400) as number }}
                data-testid={TestLocators.NoAccess_Contact_Msge}
              >
                {t('ERROR.SUB_DESCRIPTION.PHONE_TEXT')}
              </MUI.Typography>
              <CustomLink
                sx={{
                  fontSize: '16px',
                  fontWeight: (theme.typography.fontWeightRegular ?? 400) as number,
                  textTransform: 'none',
                }}
                href={`tel:${t('ERROR.SUB_DESCRIPTION.PHONE_LINK')}`}
                text={t('ERROR.SUB_DESCRIPTION.PHONE_LINK')}
                data-testid={TestLocators.NoAccess_Phone}
              />
              <MUI.Typography
                component="span"
                sx={{ fontSize: '16px', fontWeight: (theme.typography.fontWeightRegular ?? 400) as number }}
                data-testid={TestLocators.NoAccess_Or_Msge}
              >
                {t('ERROR.SUB_DESCRIPTION.EMAIL_TEXT')}
              </MUI.Typography>
              <CustomLink
                sx={{
                  fontSize: '16px',
                  fontWeight: (theme.typography.fontWeightRegular ?? 400) as number,
                  textTransform: 'none',
                }}
                href={`mailto:${t('ERROR.SUB_DESCRIPTION.EMAIL_LINK')}`}
                text={t('ERROR.SUB_DESCRIPTION.EMAIL_LINK')}
                data-testid={TestLocators.NoAccess_Email}
              />
            </div>
            {errorMessage && (
              <Accordion square expanded={expanded} onChange={toggleExpanded}>
                <Summary
                  aria-controls="error-content"
                  expandIcon={<ExpandMoreIcon sx={{ color: colors.baseGrey20 }} />}
                  data-testid={TestLocators.NoAccess_Expand_Summary}
                  theme={theme}
                >
                  <MUI.Typography data-testid={`${TestLocators.NoAccess_Error}_${expanded ? 'Open' : 'Closed'}`}>
                    {t('ERROR.EXPAND_TITLE')}
                  </MUI.Typography>
                </Summary>
                <Details>{errorMessage}</Details>
              </Accordion>
            )}
          </div>
          <div style={{ marginBottom: 24, marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <MUI.Button
              sx={{
                color: isDarkMode ? colors.primaryWhite : colors.grey40,
                position: 'relative',
                padding: '10px 24px',
                fontSize: '16px',
                '&:hover': {
                  backgroundColor: isDarkMode ? colors.white14 : colors.grey93,
                },
              }}
              onClick={handleClose}
            >
              {t('COMMON.CLOSE')}
            </MUI.Button>
          </div>
        </div>
      </div>
    </>
  );
}
